<script setup>
import { computed, onMounted, ref } from 'vue'
import { useAuthStore } from '../stores/auth.js'
import { useOrgStore } from '../stores/orgStore.js'
import { awaitScopedContextReady } from '../utils/contextReady.js'
import client from '../api/client.js'

const authStore = useAuthStore()
const orgStore = useOrgStore()

const username = ref('')
const password = ref('')
const email = ref('')
const phone = ref('')
const selectedSubOrgId = ref('')
const formError = ref('')
const loading = ref(false)
const contextReady = ref(false)

const availableSubOrgs = computed(() => orgStore.filteredSubOrgs ?? [])

const isSubmitDisabled = computed(() => {
  return (
    loading.value
    || !contextReady.value
    || !username.value.trim()
    || !password.value
    || !email.value.trim()
    || !phone.value.trim()
    || !selectedSubOrgId.value
  )
})

onMounted(async () => {
  contextReady.value = await awaitScopedContextReady(authStore, orgStore)
  if (!contextReady.value) return

  if (orgStore.activeSubOrgId) {
    selectedSubOrgId.value = String(orgStore.activeSubOrgId)
  }
})

const submit = async () => {
  formError.value = ''

  const organizationId = Number(orgStore.activeOrgId)
  const subOrgId = Number(selectedSubOrgId.value)

  if (!Number.isInteger(subOrgId) || subOrgId <= 0) {
    formError.value = 'Sub-organization is required'
    return
  }

  if (!Number.isInteger(organizationId) || organizationId <= 0) {
    formError.value = 'Organization must be selected.'
    return
  }

  loading.value = true
  try {
    const response = await client.post('/auth/register', {
      username: username.value.trim(),
      password: password.value,
      email: email.value.trim(),
      phone: phone.value.trim(),
      role: 'user',
      organization_id: organizationId,
      sub_org_id: subOrgId,
    })

    if (!response?.data) return

    username.value = ''
    password.value = ''
    email.value = ''
    phone.value = ''
  } catch (error) {
    formError.value = error?.response?.data?.detail ?? 'Registration failed.'
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <section class="register-container">
    <h2>Register User</h2>
    <p v-if="!contextReady">Loading organization context...</p>

    <form v-else @submit.prevent="submit">
      <label>
        Username
        <input v-model="username" required type="text" />
      </label>

      <label>
        Password
        <input v-model="password" required type="password" minlength="6" />
      </label>

      <label>
        Email
        <input v-model="email" required type="email" />
      </label>

      <label>
        Phone
        <input v-model="phone" required type="text" />
      </label>

      <label>
        Sub-organization
        <select v-model="selectedSubOrgId" required>
          <option disabled value="">Select sub-organization</option>
          <option v-for="subOrg in availableSubOrgs" :key="subOrg.id" :value="subOrg.id">
            {{ subOrg.name }}
          </option>
        </select>
      </label>

      <p v-if="!selectedSubOrgId" class="error">Sub-organization is required</p>

      <button type="submit" :disabled="isSubmitDisabled">
        {{ loading ? 'Registering...' : 'Register' }}
      </button>

      <p v-if="formError" class="error">{{ formError }}</p>
    </form>
  </section>
</template>

<style scoped>
.register-container {
  max-width: 520px;
  margin: 0 auto;
  display: grid;
  gap: 0.75rem;
}

form {
  display: grid;
  gap: 0.6rem;
}

.error {
  color: #b91c1c;
  background: #fee2e2;
  border-radius: 6px;
  padding: 0.45rem;
}
</style>


